/**
 * "What's your business called?" — asked of a person who signed up with a personal address.
 *
 * Maria, 5 Oct 2026: a gmail / outlook / icloud / yahoo address never names a workspace and
 * never joins one. Ask for the business name during sign-up and name the workspace with the
 * answer. A verified company address never sees this: its workspace is named after the
 * domain ("Finflare") and colleagues join it automatically.
 *
 * Nothing exists until they answer. Leaving this unanswered is the same state they were in a
 * second earlier, not a failure.
 */

import { Building2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useOrgContext } from "../providers/OrgProvider";

export const BUSINESS_NAME_MAX_LENGTH = 80;

export function BusinessNamePrompt() {
  const { businessNameRequest, submitBusinessName } = useOrgContext();

  if (!businessNameRequest) {
    return null;
  }

  return (
    <div className="border-b border-blue-500/30 bg-blue-500/10 px-6 py-3 text-sm text-blue-900 dark:text-blue-200">
      <BusinessNameForm
        rejectedName={businessNameRequest.rejectedName}
        onSubmit={submitBusinessName}
      />
    </div>
  );
}

/**
 * The question itself, with no opinion about who is asking. The banner above asks it of a
 * person who signed up; `/checkout/success` asks it of a person who just paid with a personal
 * address, before their purchase has a workspace to land in. One question, one wording.
 */
export function BusinessNameForm({
  rejectedName,
  onSubmit,
}: {
  rejectedName: string | null;
  onSubmit: (name: string) => Promise<unknown>;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!trimmed || busy) return;
    setBusy(true);
    void onSubmit(trimmed).finally(() => setBusy(false));
  };

  return (
    <form className="flex items-start gap-2" onSubmit={submit}>
      <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <label htmlFor="business-name" className="block font-medium">
          What&apos;s your business called?
        </label>
        <p>We&apos;ll name your workspace after it. You can change it later.</p>
        {rejectedName !== null && (
          <p role="alert">That name didn&apos;t work. Try a shorter one.</p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <input
            id="business-name"
            type="text"
            value={name}
            maxLength={BUSINESS_NAME_MAX_LENGTH}
            autoComplete="organization"
            onChange={(e) => setName(e.target.value)}
            className="min-w-0 flex-1 rounded-md border bg-background px-2 py-1 text-foreground"
          />
          <button
            type="submit"
            disabled={!trimmed || busy}
            className="inline-flex shrink-0 items-center rounded-md border px-2 py-1 text-xs font-medium hover:bg-background/70 disabled:opacity-60"
          >
            Create workspace
          </button>
        </div>
      </div>
    </form>
  );
}
